import { useMemo, useState } from "react";
import type { CustomerItem } from "../../../../mocks/customers";
import type { PaymentFormPayload } from "../../../../lib/salesFlow";
import MoneyInput from "../../../../components/form/MoneyInput";
import { parseMoneyInput } from "../../../../lib/money";
import type { BankTransferRecord } from "../../../../lib/bankTransferStore";
import { isBankTransferActive } from "../../../../lib/bankTransferStore";
import { matchesOptionalDateRange } from "../../../../lib/dateFilters";
import type { ShareWithAnimal } from "./ShareSelectorList";
import {
  getDisplayKupeNo,
  getDisplaySiparisNo,
} from "../../../../lib/animalLabels";

interface Props {
  customer: CustomerItem;
  selectedShares: ShareWithAnimal[];
  maxKalan: number;
  onSubmit: (data: PaymentFormPayload, linkedBankTransferIds?: string[]) => void;
  bankTransfers?: BankTransferRecord[];
}

const todayIso = new Date().toISOString().slice(0, 10);

function fmt(n: number) {
  return n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

function InputLabel({
  label,
  required,
}: {
  label: string;
  required?: boolean;
}) {
  return (
    <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-farm-muted">
      {label}
      {required && <span className="ml-0.5 text-farm-earth">*</span>}
    </label>
  );
}

export default function PaymentFormPanel({
  customer,
  selectedShares,
  maxKalan,
  onSubmit,
  bankTransfers = [],
}: Props) {
  const [tutar, setTutar] = useState("");
  const [tip, setTip] = useState<"Nakit" | "Havale">("Nakit");
  const [tarih, setTarih] = useState(todayIso);
  const [fisNo, setFisNo] = useState("");
  const [aciklama, setAciklama] = useState("");
  const [havaleNo, setHavaleNo] = useState("");
  const [error, setError] = useState("");
  const [transferPickerOpen, setTransferPickerOpen] = useState(false);
  const [selectedTransferIds, setSelectedTransferIds] = useState<string[]>([]);
  const [transferDateFrom, setTransferDateFrom] = useState("");
  const [transferDateTo, setTransferDateTo] = useState("");
  const [transferDescriptionFilter, setTransferDescriptionFilter] = useState("");

  const quickAmounts = useMemo(
    () => [
      { label: "%25", value: Math.round(maxKalan * 0.25) },
      { label: "%50", value: Math.round(maxKalan * 0.5) },
      { label: "Tamamı", value: maxKalan },
    ],
    [maxKalan]
  );

  const shareSummary = useMemo(() => {
    return selectedShares.map(({ share, animal }) => ({
      id: share.id,
      kupeNo: getDisplayKupeNo(animal),
      siparisNo: getDisplaySiparisNo(animal),
      hisseNo: share.hisseNo,
      hayvanTuru: animal.tur,
      kalan: share.kalan,
    }));
  }, [selectedShares]);

  function formatTransferDate(value?: string) {
    if (!value) return "—";
    const [year, month, day] = value.slice(0, 10).split("-");
    return year && month && day ? `${day}.${month}.${year}` : value;
  }

  function containsTransferText(value: string | undefined, query: string) {
    if (!query.trim()) return true;
    return String(value ?? "")
      .toLocaleLowerCase("tr-TR")
      .includes(query.trim().toLocaleLowerCase("tr-TR"));
  }

  const activeBankTransfers = useMemo(
    () =>
      bankTransfers
        .filter(isBankTransferActive)
        .filter((item) => {
          const dateOk = matchesOptionalDateRange(item.tarih, transferDateFrom, transferDateTo);
          const descriptionOk =
            containsTransferText(item.aciklama, transferDescriptionFilter) ||
            containsTransferText(item.fisNo, transferDescriptionFilter);
          return dateOk && descriptionOk;
        }),
    [bankTransfers, transferDateFrom, transferDateTo, transferDescriptionFilter]
  );

  function toggleTransferSelection(id: string) {
    setSelectedTransferIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  }

  function addSelectedTransfersToPayment() {
    const selected = activeBankTransfers.filter((item) =>
      selectedTransferIds.includes(item.id)
    );

    if (selected.length === 0) {
      setError("Aktarmak için en az bir havale seç.");
      return;
    }

    const totalCents = selected.reduce(
      (sum, item) => sum + Math.round(Number(item.tutar || 0) * 100),
      0
    );
    const total = Math.round(totalCents / 100);

    setTip("Havale");
    setTutar(String(total));
    setHavaleNo(selected.map((item) => item.fisNo).join(", "));
    setTransferPickerOpen(false);
    setError("");
  }

  function handleSubmit() {
    const amount = parseMoneyInput(tutar);

    if (!tarih) {
      setError("Tarih zorunludur.");
      return;
    }

    if (!tutar || Number.isNaN(amount) || amount <= 0) {
      setError("Geçerli bir tutar giriniz.");
      return;
    }

    // Kapora / fazla ödeme serbesttir. Tutar seçili kalan borcu aşabilir.
    // Fazla kısım seçili ilk hisseye alacak/eksi bakiye olarak işlenir.

    if (tip === "Havale" && !havaleNo.trim()) {
      setError("Havale / dekont no giriniz.");
      return;
    }

    setError("");

    onSubmit(
      {
        tarih,
        tutar: amount,
        tip,
        aciklama,
        havaleNo,
        fisNo,
      },
      selectedTransferIds
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-farm border border-farm-border bg-farm-warm px-3 py-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-farm-muted">
              Ödeme Alınan Müşteri
            </div>
            <div className="mt-1 text-sm font-semibold text-farm-brown">
              {customer.adSoyad}
            </div>
            <div className="mt-0.5 text-xs text-farm-muted">{customer.telefon}</div>
          </div>

          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wide text-farm-muted">
              Seçili Kalan
            </div>
            <div className="mt-1 text-base font-bold text-farm-earth">
              {fmt(maxKalan)}
            </div>
          </div>
        </div>
      </div>

      {shareSummary.length > 0 && (
        <div className="overflow-hidden rounded-farm border border-farm-border bg-white">
          <div className="border-b border-farm-border bg-farm-cream px-3 py-2">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-farm-muted">
              Seçilen Hisseler
            </p>
          </div>

          <div className="divide-y divide-farm-border">
            {shareSummary.map((item) => (
              <div
                key={item.id}
                className="flex items-start justify-between gap-3 px-3 py-2.5"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className="text-sm font-semibold text-farm-brown">
                      {item.kupeNo}
                    </div>
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${item.hayvanTuru === "Büyükbaş" ? "bg-farm-earth-pale text-farm-earth" : "bg-farm-green-pale text-farm-green"}`}>
                      {item.hayvanTuru === "Büyükbaş" ? "B.Baş" : "K.Baş"}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-farm-muted">
                    Sipariş No:{" "}
                    <span className="font-semibold text-farm-brown">
                      {item.siparisNo}
                    </span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-farm-muted">
                    Hisse:{" "}
                    <span className="font-semibold text-farm-brown">
                      {item.hisseNo}
                    </span>
                  </div>
                </div>

                <div className="shrink-0 text-right">
                  <div className="text-[10px] uppercase tracking-wide text-farm-muted">
                    Kalan
                  </div>
                  <div className="mt-0.5 text-sm font-bold text-farm-earth">
                    {fmt(item.kalan)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <InputLabel label="Hızlı Seçim" />
        <div className="grid grid-cols-3 gap-2">
          {quickAmounts.map((item) => (
            <button
              key={item.label}
              type="button"
              onClick={() => {
                setTutar(String(item.value));
                setError("");
              }}
              className={`rounded-xl border px-2 py-2 text-xs font-semibold transition-colors ${
                tutar === String(item.value)
                  ? "border-farm-earth bg-farm-earth text-white"
                  : "border-farm-border bg-white text-farm-brown hover:border-farm-earth/40"
              }`}
            >
              <div>{item.label}</div>
              <div className="mt-0.5 text-[11px] opacity-90">{fmt(item.value)}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <InputLabel label="Tarih" required />
          <input
            type="date"
            value={tarih}
            onChange={(e) => {
              setTarih(e.target.value);
              setError("");
            }}
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
        </div>

        <div>
          <InputLabel label="Fiş No" />
          <input
            type="text"
            value={fisNo}
            onChange={(e) => setFisNo(e.target.value)}
            placeholder="OPS-2026-0001"
            className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
          />
        </div>
      </div>

      <div>
        <InputLabel label="Ödeme Tutarı" required />
        <MoneyInput
          value={tutar}
          onChange={(value) => {
            setTutar(value);
            setError("");
          }}
          placeholder="0"
          className="h-11 rounded-xl"
        />
      </div>

      <div>
        <InputLabel label="Ödeme Tipi" />
        <div className="grid grid-cols-2 gap-2">
          {(["Nakit", "Havale"] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setTip(value)}
              className={`h-11 rounded-xl border text-sm font-semibold transition-colors ${
                tip === value
                  ? value === "Nakit"
                    ? "border-farm-green bg-farm-green text-white"
                    : "border-sky-600 bg-sky-600 text-white"
                  : "border-farm-border bg-white text-farm-brown hover:border-farm-earth/40"
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      {tip === "Havale" && (
        <div className="rounded-farm border border-sky-100 bg-sky-50/60 px-3 py-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <InputLabel label="Havale Aktar" />
              <p className="text-[11px] text-farm-muted">
                Aktif havalelerden bir veya birkaçını seçip ödeme tutarına aktar.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setTransferPickerOpen(true)}
              className="h-9 rounded-xl bg-sky-600 px-3 text-xs font-semibold text-white hover:bg-sky-700"
            >
              Havale Aktar
            </button>
          </div>
          {selectedTransferIds.length > 0 && (
            <div className="mt-2 rounded-xl border border-sky-100 bg-white px-3 py-2 text-[11px] text-sky-700">
              {selectedTransferIds.length} havale seçildi. Kaydedince bu kayıtlar otomatik dolduruldu olarak pasife alınır.
            </div>
          )}
        </div>
      )}

      <div>
        <InputLabel label="Havale / Dekont No" required={tip === "Havale"} />
        <input
          type="text"
          value={havaleNo}
          onChange={(e) => {
            setHavaleNo(e.target.value);
            setError("");
          }}
          placeholder="İsteğe bağlı referans no"
          className="h-11 w-full rounded-xl border border-farm-border bg-farm-cream px-3 text-sm text-farm-brown outline-none focus:border-farm-earth"
        />
      </div>

      <div>
        <InputLabel label="Açıklama" />
        <textarea
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
          placeholder="Ödeme açıklaması"
          rows={3}
          className="w-full resize-none rounded-xl border border-farm-border bg-farm-cream px-3 py-2.5 text-sm text-farm-brown outline-none focus:border-farm-earth"
        />
      </div>

      {error && (
        <div className="rounded-farm border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        className="h-11 w-full rounded-xl bg-farm-earth text-sm font-semibold text-white hover:bg-farm-brown"
      >
        Ödemeyi Kaydet
      </button>

      {transferPickerOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-3">
          <div className="w-full max-w-4xl rounded-xl bg-white p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <div>
                <h4 className="text-sm font-bold text-gray-800">Aktif Havale Aktar</h4>
                <p className="text-[10px] text-gray-500">
                  Boşta duran aktif havaleleri bu ödeme kaydına aktarır. Birden fazla kutucuğu seçebilirsin.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTransferPickerOpen(false)}
                className="h-8 w-8 rounded-full text-gray-400 hover:bg-gray-100"
              >
                <i className="ri-close-line" />
              </button>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
              <input
                type="date"
                value={transferDateFrom}
                onChange={(event) => setTransferDateFrom(event.target.value)}
                className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
                title="Başlangıç tarihi"
              />
              <input
                type="date"
                value={transferDateTo}
                onChange={(event) => setTransferDateTo(event.target.value)}
                className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
                title="Bitiş tarihi"
              />
              <input
                value={transferDescriptionFilter}
                onChange={(event) => setTransferDescriptionFilter(event.target.value)}
                placeholder="Açıklama / Fiş No içinde ara"
                className="h-10 rounded-lg border border-gray-200 px-3 text-xs outline-none focus:border-sky-400"
              />
            </div>

            <div className="mt-3 max-h-[360px] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-100">
              {activeBankTransfers.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-gray-500">
                  Aktif havale yok.
                </div>
              ) : (
                activeBankTransfers.map((item) => {
                  const checked = selectedTransferIds.includes(item.id);

                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleTransferSelection(item.id)}
                      className={`grid w-full grid-cols-[28px_86px_86px_minmax(0,1fr)_100px] items-start gap-2 px-3 py-2.5 text-left text-xs hover:bg-sky-50 ${
                        checked ? "bg-sky-50" : ""
                      }`}
                    >
                      <span className="pt-0.5">
                        <span
                          className={`flex h-4 w-4 items-center justify-center rounded border ${
                            checked
                              ? "border-sky-600 bg-sky-600 text-white"
                              : "border-gray-300 bg-white"
                          }`}
                        >
                          {checked && <i className="ri-check-line text-xs" />}
                        </span>
                      </span>
                      <span className="font-semibold text-gray-500">
                        {formatTransferDate(item.tarih)}
                      </span>
                      <span className="font-bold text-sky-700">{item.fisNo}</span>
                      <span className="whitespace-normal break-words leading-snug text-gray-700">
                        {item.aciklama || "—"}
                      </span>
                      <span className="text-right font-bold text-gray-800">
                        {item.tutar.toLocaleString("tr-TR", {
                          style: "currency",
                          currency: "TRY",
                          maximumFractionDigits: 0,
                        })}
                      </span>
                    </button>
                  );
                })
              )}
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
              <p className="text-[10px] text-gray-500">
                {selectedTransferIds.length} havale seçildi. Seçili tutar ödeme alanına aktarılır; açıklama alanına otomatik yazı basılmaz.
              </p>
              <button
                type="button"
                onClick={addSelectedTransfersToPayment}
                disabled={selectedTransferIds.length === 0}
                className="rounded-full bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Seçili Havaleleri Aktar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
